import api from '@/configurations/service';
import FormatMixin from '@/modules/Application/mixins/FormatMixin';
import { consultTypeEnum } from '@/modules/Fiscal/modules/Nfse/enums/consultTypeEnum';
import * as types from './mutation-types';
import { serializeNfseForSave } from '../parsers/serializeNfseForSave';

const { valorUS } = FormatMixin.methods;

export const getFiscalValidation = async (context) => {
  const { data } = await api().post('consumers.fiscal-validation-nfse');

  context.commit(types.SET_FISCAL_VALIDATION, data);
};

export const setLastNatureOfOperation = async (context) => {
  const { data } = await api().post('consumers.find-last-nfse-nature-of-operation');

  if (!data) return;

  context.commit(types.SET_NATURE_OF_OPERATION, data);
};

export const setSummaryNfses = async (context, { search, pagination }) => {
  const body = { search, pagination };
  const { data } = await api().post('consumers.find-summary-nfses', body);

  context.commit(types.SET_PAGINATION_NFSE, data.pagination);
  context.commit(types.SET_SUMMARY_NFSES, data.data);
};

export const applyEmissionConfigurations = async ({ rootGetters, commit }) => {
  const configurations = rootGetters['nfseConfigurations/getNfseConfigurations'];
  const activeSerie = configurations.series.find((e) => e.active);

  const payload = { ...configurations, serie: activeSerie };

  commit(types.SET_EMISSION_CONFIGURATIONS, payload);
};

export const setDetailedNfse = async (context, id) => {
  const { data } = await api().post('consumers.find-nfse', { id });

  context.commit(types.SET_DETAILED_NFSE, data);
};

export const registerNfse = async (context, payload) => {
  const nfseData = serializeNfseForSave(payload);

  const { data } = await api().post('consumers.register-nfse', nfseData);

  return data;
};

export const updateNfse = async (context, payload) => {
  const nfseData = serializeNfseForSave(payload);

  const { data } = await api().post('consumers.update-nfse', nfseData);

  return data;
};

export const transmitNfse = async (_, id) => {
  await api().post('consumers.transmit-nfse', id);
};

export const consultNfse = async (_, id) => {
  await api().post('consumers.consult-nfse', {
    id,
    consultType: consultTypeEnum.SITUATION,
  });
};

export const cancelNfse = async (_, payload) => {
  await api().post('consumers.cancel-nfse', payload);
};

export const resetNfseState = async (context) => {
  context.commit(types.RESET_NFSE);
};

export const findXmlUrl = async (_, id) => {
  const { data } = await api().post('consumers.find-nfse-xml', { id });

  return data.xmlUrl;
};

export const generateDanfse = async (_, id) => {
  const { data } = await api().post('consumers.generate-danfse', { id });

  return data.danfseUrl;
};

export const sendNfseEmail = async (_, id) => {
  await api().post('consumers.send-nfse-email', { id });
};

export const setServiceInCollection = async ({ commit, getters }, payload) => {
  const { data } = await api().post('consumers.find-detailed-service', {
    id: payload.remoteServiceId,
  });

  delete data.fiscal.id;
  delete data.fiscal.serviceId;

  const serviceCollection = [...getters.serviceCollection];
  const service = { ...data, ...payload, price: valorUS(payload.price) };

  serviceCollection.push(service);

  commit(types.SET_SERVICE_IN_COLLECTION, serviceCollection);
};

export const updateServiceInCollection = async ({ commit, getters }, payload) => {
  const serviceCollection = [...getters.serviceCollection];

  serviceCollection.splice(payload.index, 1, payload);

  commit(types.UPDATE_SERVICE_IN_COLLECTION, serviceCollection);
};

export const setRemainder = async ({ getters, commit }) => {
  const { totalNfse, paymentModeCollection, serviceCollection } = getters;

  if (serviceCollection <= 0) commit(types.SET_REMAINDER, 0);

  const totalPayment = paymentModeCollection.reduce((acc, item) => acc + (item.value ?? 0), 0);

  const remainder = totalNfse - totalPayment;

  if (remainder <= 0) {
    commit(types.SET_REMAINDER, 0);
  } else {
    commit(types.SET_REMAINDER, remainder);
  }
};

export const calculateNfse = async ({ getters, commit }, payload) => {
  const { nfse } = getters;

  const toSerialize = { ...nfse, taxes: { ...nfse.taxes, calcTypes: payload } };
  const serializedNfse = serializeNfseForSave(toSerialize);

  const { data } = await api().post('consumers.calculate-total-nfse', serializedNfse);

  commit(types.SET_CALCULATED_NFSE, data);
};

export const applyTaxesConfigurations = async ({ rootGetters, commit }) => {
  const {
    inssPercentage,
    irPercentage,
    pisPercentage,
    cofinsPercentage,
    csllPercentage,
  } = rootGetters['nfseConfigurations/getNfseConfigurations'];

  const payload = {
    inssPercentage,
    irPercentage,
    pisPercentage,
    cofinsPercentage,
    csllPercentage,
  };

  commit(types.SET_TAXES_CONFIGURATIONS, payload);
};

export const hasInvalidCollapse = (_, { collapsesToValidate, refs, root }) =>
  collapsesToValidate.some((e) => {
    const collapseClass = refs[e].$el.children[0].children[1].classList.value;
    const isInvalidCollapse = refs[e].$v.$invalid;

    if (isInvalidCollapse && collapseClass.includes('show')) return true;
    if (isInvalidCollapse && !collapseClass.includes('show')) {
      root.$emit('bv::toggle::collapse', e);
      return true;
    }

    return false;
  });
